import React from "react";
import {
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { CircleHelp, PlusIcon } from "lucide-react";

// Static/non-static only meaningful for Retsinformation (LBK supersession).
// See CreateDocumentLinkTemplateRequestModel.IsDestinationStatic.
const REFERENCE_TYPE_TOOLTIP = (
  <div className="space-y-2 text-sm">
    <p>
      Gælder kun for{" "}
      <span className="font-medium">Retsinformation-dokumenter</span>.
    </p>
    <p>
      <span className="font-medium">Dynamisk reference</span> opdateres
      typisk til den nye gældende tekst, når en erstatningslov gør denne udgave
      historisk — så referencen følger med til den aktuelle version.
    </p>
    <p>
      <span className="font-medium">Statisk reference</span> forbliver knyttet
      til denne konkrete lovtekst, også når der udkommer en ny lovbekendtgørelse
      (LBK), der erstatter denne, og denne udgave bliver historisk.
    </p>
  </div>
);

interface ReferenceTypePickerProps {
  value: "static" | "nonstatic";
  onChange: (next: "static" | "nonstatic") => void;
  onConfirm: () => void;
  isPending: boolean;
  /** Extra disable condition (e.g. missing source/docId). Does not show a spinner. */
  disabled?: boolean;
  /** Unique prefix for element ids when multiple instances live on the same page. */
  idPrefix?: string;
}

export const ReferenceTypePicker: React.FC<ReferenceTypePickerProps> = ({
  value,
  onChange,
  onConfirm,
  isPending,
  disabled = false,
  idPrefix = "reference-type",
}) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-foreground">
            Referencetype
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                id={`${idPrefix}-help`}
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none ring-offset-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Forklaring af dynamisk og statisk reference"
                tabIndex={0}
              >
                <CircleHelp className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm text-left" sideOffset={6}>
              {REFERENCE_TYPE_TOOLTIP}
            </TooltipContent>
          </Tooltip>
        </div>
        <RadioGroup
          id={`${idPrefix}-radio-group`}
          value={value}
          onValueChange={(v) => {
            if (v === "static" || v === "nonstatic") {
              onChange(v);
            }
          }}
          className="gap-2"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="nonstatic" id={`${idPrefix}-nonstatic`} />
            <Label
              htmlFor={`${idPrefix}-nonstatic`}
              className="cursor-pointer text-sm font-normal leading-snug"
              onPointerDown={(e) => e.preventDefault()}
            >
              Dynamisk reference
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="static" id={`${idPrefix}-static`} />
            <Label
              htmlFor={`${idPrefix}-static`}
              className="cursor-pointer text-sm font-normal leading-snug"
              onPointerDown={(e) => e.preventDefault()}
            >
              Statisk reference
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Button
        className="shadow-md"
        size="lg"
        disabled={disabled || isPending}
        onClick={onConfirm}
        id={`${idPrefix}-confirm-button`}
      >
        {isPending ? (
          <Spinner size="small" className="mr-2" />
        ) : (
          <PlusIcon className="h-4 w-4 mr-2" />
        )}
        Opret reference
      </Button>
    </div>
  );
};
